import { cn } from "@/lib/utils";
import { parse, ParseResult } from "papaparse";
import { useEffect, useState } from "react";

import { Loader2 } from "lucide-react";
import { useDropzone } from "react-dropzone";

export type CsvResult = ParseResult<string[]>;

type Props = {
  setParseResults: (result: CsvResult) => void;
  className?: string;
  children?: React.ReactNode;
};

function CsvUpload({ setParseResults, className, children }: Props) {
  const { acceptedFiles, getRootProps, getInputProps, isFocused, isDragActive } = useDropzone({
    maxFiles: 1,
    accept: {
      "text/csv": [".csv"],
    },
  });

  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (acceptedFiles.length > 0) {
      await setUploading(true);
      const file = acceptedFiles[0];

      parse(file, {
        worker: true,
        skipEmptyLines: true,
        complete: async function (result: CsvResult) {
          const withoutHeaderLine = result.data.slice(1);
          result.data = withoutHeaderLine;
          setParseResults(result);
          await setUploading(false);
        },
      });
    }
  };

  useEffect(() => {
    handleUpload();
  }, [acceptedFiles]);

  return (
    <div className={className}>
      <div
        {...getRootProps({
          className: cn(
            "h-48 border border-dashed border-2 border-border rounded-md p-8 flex flex-col items-center justify-center cursor-pointer text-muted-foreground transition-all duration-300 hover:bg-muted hover:text-foreground hover:border-foreground",
            isFocused && "border-foreground bg-muted text-foreground",
            isDragActive && "border-foreground bg-muted text-foreground",
          ),
        })}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center justify-center space-y-6">
          {children}
          {uploading && <Loader2 className="animate-spin text-muted-foreground" />}
        </div>
      </div>
    </div>
  );
}

export default CsvUpload;
